import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Alert, AlertTitle, Box, CircularProgress } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import CheckIcon from "@mui/icons-material/Check";
import ConfirmationDialog from "../../../components/ui/ConfirmationDialog";
import useApi from "../../../hooks/useApi";
import usePifDraft from "../../../hooks/usePifDraft";
import { useUser } from "../../../context/UserContext";
import { getPostIfEmploymentHistory } from "../../../services/pifService";
import { PifReturnNote, derivePifReturnNote } from "../../../services/pifListApproval";
import { PostIfEmploymentHistoryDto } from "../../../types/pifDto";
import { PIF_FLOW, SEVERITIES } from "../../../data/constants";
import { showAlert } from "../../../redux/alertSlice";
import PifBreadcrumbs from "./PifBreadcrumbs";
import PifStartScreen from "./start/PifStartScreen";
import PifWorkspace from "./workspace/PifWorkspace";
import PifDoneScreen, { PifDoneChainItem } from "./done/PifDoneScreen";
import {
  PIF_BLANK_FORM,
  PifCandidate,
  PifEavValues,
  PifFlow,
  PifFormValues,
  PifScreen,
  REOPENABLE_PIF_STATUSES,
} from "./types";

/** Portal_PostIF.Flow for the confirmed person — the approval chain branches on it. */
const FLOW_TO_API: Record<PifFlow, string> = {
  default: PIF_FLOW.NEW_HIRE,
  new: PIF_FLOW.NEW_HIRE,
  active: PIF_FLOW.EXISTING_ACTIVE,
  inactive: PIF_FLOW.REHIRE,
};

/** Reverse of FLOW_TO_API, for re-opening a saved draft. */
const API_TO_FLOW: Record<string, PifFlow> = {
  [PIF_FLOW.NEW_HIRE]: "new",
  [PIF_FLOW.EXISTING_ACTIVE]: "active",
  [PIF_FLOW.REHIRE]: "inactive",
};

/** Reverse of PifWorkspace's HIRE_TYPE_TO_SWITCH_OR_ADDITION. */
const SWITCH_OR_ADDITION_TO_HIRE_TYPE: Record<string, string> = {
  addition: "Additional position (keeps current one)",
  switch: "Position change (replaces current one)",
};

const LIST_URL = "/supervision/postif";

/**
 * The employee's CURRENT employment type, read off their active employment-history row.
 *
 * The identity lookup doesn't return it (PostIfIdentityLookupResultDto has no such field), so the
 * history is the only place an existing person's FT/PT arrives — without this the form always
 * opened on PIF_BLANK_FORM's "Part-time", even for someone plainly full-time. HR_EmploymentHistory
 * spells it "Full Time"/"Part Time"; matched loosely so a "Full-Time"/"FULLTIME" variant still
 * lands. Null means "the history doesn't say", which must leave the blank-form default alone
 * rather than assert part-time.
 */
const empTypeFromHistory = (rows: PostIfEmploymentHistoryDto[]): PifFormValues["empType"] | null => {
  const current = rows.find((row) => row.isActive);
  const value = (current?.employmentType ?? "").replace(/[\s-]/g, "").toLowerCase();
  if (value.includes("full")) return "Full-time";
  if (value.includes("part")) return "Part-time";
  return null;
};

/**
 * The post-submit chain. The first row is the person who just signed, so it has to be the real
 * signed-in user — it was a hardcoded name, which put someone else's signature on every
 * supervisor's receipt (PR 30856 review, Pooja Pujara). Resolved exactly as PifReviewDrawer does,
 * since that drawer is where the same person signed a moment earlier.
 */
const doneChain = (signerName: string): PifDoneChainItem[] => [
  { name: signerName, detail: "Signed", state: "done" },
  { name: "Department Director", detail: "Pending — notified just now", state: "current" },
  { name: "HR Director", detail: "Approves or declines · reviews rate and band", state: "waiting" },
  { name: "Executive Director", detail: "Final signature · copy returns to you", state: "waiting" },
];

const PostInterviewForm: FC = () => {
  const { executeApi } = useApi();
  const dispatch = useDispatch();
  const { firstName, lastName, userName } = useUser();
  const [screen, setScreen] = useState<PifScreen>("start");
  const [flow, setFlow] = useState<PifFlow>("default");
  const [values, setValues] = useState<PifFormValues>({ ...PIF_BLANK_FORM });
  const [eav, setEav] = useState<PifEavValues>({});
  const [candidate, setCandidate] = useState<PifCandidate | null>(null);
  const [history, setHistory] = useState<PostIfEmploymentHistoryDto[]>([]);
  const [signed, setSigned] = useState<{ refNo: string; signedAt: string } | null>(null);
  const [reopenError, setReopenError] = useState<string | null>(null);
  // Set only for a form reopened in 'changes_requested' - the approver's note, so the supervisor
  // knows what to fix instead of guessing at a form that silently came back.
  const [returnNote, setReturnNote] = useState<PifReturnNote | null>(null);
  const [edited, setEdited] = useState(false);
  // Set when a reopened draft carried its own employment_type answer, so the employment-history
  // seed below leaves it alone. A ref, not state: it only ever gates an effect.
  const savedEmpType = useRef(false);
  const [confirmExit, setConfirmExit] = useState(false);
  const { draft, catalogue, opening, open, ensureCreated, load, reset: resetDraft } = usePifDraft();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const reopenId = Number(id);
  const isReopen = !!reopenId && !Number.isNaN(reopenId);
  // Hold the whole screen while re-opening, so the start gate never shows for a saved draft.
  // Owned by the reopen effect below rather than derived from `draft`: usePifDraft.load sets
  // `draft` as soon as the read returns, which is before this component has moved off the
  // "start" screen — and under StrictMode's double-mount the first pass leaves `draft` set
  // while the second pass is still calling can-act + get, so the lookup gate rendered for the
  // whole of that second load instead of the draft the supervisor clicked Edit on.
  const [hydrating, setHydrating] = useState(isReopen);

  const handleFieldChange = useCallback((patch: Partial<PifFormValues>) => {
    setEdited(true);
    setValues((prev) => ({ ...prev, ...patch }));
  }, []);

  const handleEavChange = useCallback((fieldKey: string, value: string) => {
    setEdited(true);
    setEav((prev) => ({ ...prev, [fieldKey]: value }));
  }, []);

  /**
   * Epic 79822's leave-without-saving guard. Leaving unmounts the form and drops everything
   * typed, so only skip the prompt when nothing has been entered yet. `edited` is cleared on
   * a successful save, so a saved draft exits without nagging.
   *
   * Nothing has to be cleaned up on the way out any more: since Bug 79824 the Portal_PostIF row
   * is only written on the first save, so a form the supervisor abandons never existed.
   */
  const handleExit = useCallback(() => {
    if (edited) {
      setConfirmExit(true);
      return;
    }
    navigate(LIST_URL);
  }, [edited, navigate]);

  const handleConfirmExit = useCallback(
    (confirmed: boolean) => {
      setConfirmExit(false);
      if (confirmed) navigate(LIST_URL);
    },
    [navigate],
  );

  /** Stable so it doesn't re-create PifWorkspace's persistDraft callback on every render. */
  const handleSaved = useCallback(() => setEdited(false), []);

  /**
   * The lookup found an open draft this user already owns for the person, so reopen it at its
   * own URL instead of creating a duplicate. Routing here (rather than hydrating inline) reuses
   * the reopen effect below, which restores applicant, contact, kind-of-hire and every answer.
   *
   * Must be `/:id/edit`, not `/:id`: since Epic 79822, the bare id renders the read-only
   * PostIFApproval detail screen, and only `/edit` renders this form.
   */
  const handleResume = useCallback(
    (postIfId: number) => {
      navigate(`${LIST_URL}/${postIfId}/edit`);
    },
    [navigate],
  );

  /**
   * The person is confirmed, so this is where the record starts existing: create-draft
   * inserts the header and takes the soft record-lock. If it fails, useApi has already
   * raised the alert — stay on the start screen rather than opening a form that cannot save.
   */
  const handleConfirmed = useCallback(
    async (nextFlow: PifFlow, cand: PifCandidate | null) => {
      const opened = await open({
        flow: FLOW_TO_API[nextFlow],
        postIfTitle: cand ? `${cand.first ?? ""} ${cand.last ?? ""}`.trim() || null : null,
        deptPositionId: null,
      });
      if (!opened) return;

      setCandidate(cand);
      setFlow(nextFlow);
      if (nextFlow === "new") {
        setValues((prev) => ({ ...prev, hireType: "New employee" }));
      } else if (cand) {
        setValues((prev) => ({
          ...prev,
          firstName: cand.first || "",
          middleName: cand.middle || "",
          lastName: cand.last || "",
          dob: cand.dob || "",
          addr1: cand.addr1 || "",
          city: cand.city || "",
          state: cand.state || "NY",
          zip: cand.zip || "",
          home: cand.home || "",
          cell: cand.cell || "",
          email: cand.email || "",
          hireType: nextFlow === "active" ? "Additional position (keeps current one)" : "New employee",
        }));
      }
      setScreen("form");
    },
    [open],
  );

  /**
   * Re-open an existing PostIF from /supervision/postif/:id.
   *
   * Restores the applicant, contact, kind-of-hire and every saved dynamic answer. It does
   * NOT restore Department/Position: `Portal_PostIF` stores only `DeptPositionId`, and no
   * endpoint maps a position back to its department, so the two dropdowns can't be
   * repopulated — the supervisor re-picks them. (The header write must therefore leave
   * DeptPositionId alone when it arrives as null, or re-saving would wipe it.)
   *
   * A form that is no longer a draft is read-only per the Epic, so it lands on the
   * signed-and-sent screen rather than an editable form.
   */
  useEffect(() => {
    if (!reopenId || Number.isNaN(reopenId)) return;
    let active = true;

    const reopen = async () => {
      const detail = await load(reopenId);
      if (!active) return;
      if (!detail) {
        setReopenError("This Post Interview Form can't be opened. It may not exist, or it isn't yours to edit.");
        setHydrating(false);
        return;
      }

      const { header, applicant, answers } = detail;
      setReturnNote(derivePifReturnNote(detail));
      setFlow(API_TO_FLOW[header.flow ?? ""] ?? "new");
      const savedAnswers = (answers ?? []).reduce<PifEavValues>((map, answer) => {
        if (answer.fieldKey && typeof answer.value === "string") map[answer.fieldKey] = answer.value;
        return map;
      }, {});
      setEav(savedAnswers);
      if (savedAnswers.employment_type) savedEmpType.current = true;

      // Mirror of PifWorkspace's derived-answer bridge. These three keys live in the EAV
      // catalogue but are rendered by hardcoded controls bound to `f`, so without mapping them
      // back they reload blank even though they saved correctly.
      setValues((prev) => ({
        ...prev,
        // Position needs its department set first, or the Position dropdown stays disabled.
        deptId: header.departmentId ?? prev.deptId,
        posId: header.deptPositionId ?? prev.posId,
        rate: savedAnswers.requested_rate ?? prev.rate,
        startDate: savedAnswers.desired_start_date ?? prev.startDate,
        notes: savedAnswers.department_notes ?? prev.notes,
        hours: savedAnswers.hours_per_week ?? prev.hours,
        // Stored as the label. Anything other than an exact "Full-time" (including a draft saved
        // before the bridge existed, which has no answer at all) falls back to the blank form's
        // Part-time rather than widening the union.
        empType: savedAnswers.employment_type === "Full-time" ? "Full-time" : prev.empType,
        // Stored as the id; the matching label is backfilled from the rate-type lookup in
        // PifWorkspace once it loads, since this screen has no reference data of its own.
        rateTypeId: savedAnswers.rate_type ? Number(savedAnswers.rate_type) : prev.rateTypeId,
        // serviceId must be restored for the Client dropdown to re-enable at all - it is
        // disabled={!f.serviceId} and its options are fetched by GetClientsByService(serviceId).
        serviceId: savedAnswers.service ? Number(savedAnswers.service) : prev.serviceId,
        clientId: savedAnswers.client ? Number(savedAnswers.client) : prev.clientId,
        reportsToId: savedAnswers.reports_to ? Number(savedAnswers.reports_to) : prev.reportsToId,
      }));

      if (applicant) {
        setValues((prev) => ({
          ...prev,
          firstName: applicant.firstName ?? "",
          middleName: applicant.middleName ?? "",
          lastName: applicant.lastName ?? "",
          knownAs: applicant.knownName ?? "",
          dob: applicant.dob ? applicant.dob.slice(0, 10) : "",
          addr1: applicant.address1 ?? "",
          city: applicant.city ?? "",
          state: applicant.state ?? "NY",
          zip: applicant.zip ?? "",
          home: applicant.homePhone ?? "",
          cell: applicant.cell ?? "",
          email: applicant.email ?? "",
          hireType: SWITCH_OR_ADDITION_TO_HIRE_TYPE[header.switchOrAddition ?? ""] ?? "New employee",
        }));
        if (applicant.enterpriseEmployeeId) {
          setCandidate({
            status: header.flow === PIF_FLOW.EXISTING_ACTIVE ? "active" : "inactive",
            employeeId: applicant.enterpriseEmployeeId,
            first: applicant.firstName ?? "",
            last: applicant.lastName ?? "",
          });
        }
      }

      // 'changes_requested' reopens EDITABLE - that is the whole point of returning a form. Only
      // a genuinely locked status (submitted / approved / declined) lands on the read-only screen.
      // The API agrees: both IU procs and Submit_U accept 'draft' and 'changes_requested' alike.
      if (header.status && !REOPENABLE_PIF_STATUSES.includes(header.status.toLowerCase())) {
        setSigned({ refNo: header.refNo ?? "", signedAt: "" });
        setScreen("done");
        setHydrating(false);
        return;
      }
      setScreen("form");
      setHydrating(false);
    };

    void reopen();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reopenId]);

  // The employment history behind the "Already at Hamaspik" card. Only an existing person
  // has one, and it is advisory — a failure must not stop the form.
  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!candidate?.employeeId) {
        setHistory([]);
        return;
      }
      const rows = await executeApi<number, PostIfEmploymentHistoryDto[]>(
        getPostIfEmploymentHistory,
        candidate.employeeId,
      );
      if (!active) return;
      setHistory(rows ?? []);

      // Seed the FT/PT toggle from what they're working today. Skipped once the draft's own saved
      // answer has been restored — that was an explicit choice about the NEW position and outranks
      // the current one. PifWorkspace clamps this back to Part-time if the position finally chosen
      // can't be full-time, so seeding "Full-time" here can't smuggle an invalid value through.
      if (!savedEmpType.current) {
        const fromHistory = empTypeFromHistory(rows ?? []);
        if (fromHistory) setValues((prev) => ({ ...prev, empType: fromHistory }));
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, [executeApi, candidate?.employeeId]);

  const handleRestart = useCallback(() => {
    setValues({ ...PIF_BLANK_FORM });
    setEav({});
    setCandidate(null);
    setHistory([]);
    setFlow("default");
    setSigned(null);
    setReturnNote(null);
    // Clears the id AND the pending create request, so "Start another" begins a genuinely new
    // form rather than saving the next person's answers onto the one just submitted.
    resetDraft();
    setEdited(false);
    setScreen("start");
  }, [resetDraft]);

  const handleSigned = useCallback((refNo: string, signedAt: string) => {
    setSigned({ refNo, signedAt });
    setScreen("done");
  }, []);

  /**
   * The done screen is a STATE of /supervision/postif/new, not a route of its own, so
   * window.location.href is still the blank-form URL here — copying it handed the supervisor a
   * link that started a NEW form instead of opening the record they had just submitted. Copy the
   * record's own read-only route: `/:id` admits the creator (sProc_Portal_PostIF_CanAct_S 'open'),
   * while `/:id/edit` is locked once the status is 'submitted'.
   *
   * Every path now reports back. The old version was silent on success and on failure alike, so a
   * working copy and a missing clipboard API looked identical — which is why this read as "not
   * working" even where it did copy.
   */
  const handleCopyLink = useCallback(() => {
    const postIfId = draft?.postIfId;
    if (!postIfId) {
      dispatch(showAlert({ message: "This form has no shareable link yet.", severity: SEVERITIES.ERROR }));
      return;
    }

    const url = `${window.location.origin}${LIST_URL}/${postIfId}`;
    const fallback = () =>
      dispatch(showAlert({ message: `Couldn't copy automatically. The link is ${url}`, severity: SEVERITIES.WARNING }));

    // `navigator.clipboard` is undefined on a non-secure origin, and writeText rejects when the
    // document isn't focused or permission is denied — both fall back to showing the link.
    const copying = navigator.clipboard?.writeText(url);
    if (!copying) {
      fallback();
      return;
    }
    copying.then(
      () => dispatch(showAlert({ message: "Link copied to clipboard.", severity: SEVERITIES.SUCCESS })),
      fallback,
    );
  }, [draft?.postIfId, dispatch]);

  // No PDF-generation backend exists yet — the browser's print dialog (with
  // "Save as PDF" as a destination) is the closest real behavior available.
  const handleDownloadPdf = () => window.print();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="postif-shell" sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        {/* Above the screens, so it shows on the gate, the form and the done screen alike. It
            scrolls away under PifWorkspace's sticky header rather than fighting it for the top
            slot — that header's offset is measured off the shell header, not off this. */}
        <PifBreadcrumbs current={isReopen ? "Edit draft" : "New PostIF"} />

        {/* Sits above the workspace, aligned to its grid (maxWidth mirrors $pif-content-max-width).
            Gated on the form screen so it never trails the supervisor onto the done screen after a
            successful re-submit. */}
        {screen === "form" && returnNote && (
          <Alert
            severity="warning"
            sx={{ maxWidth: 1560, mx: "auto", mt: 1, mb: -0.5 }}
            data-testid="postif-returned-note"
          >
            <AlertTitle sx={{ mb: 0.25 }}>
              Returned for changes by the {returnNote.by}
              {returnNote.when ? ` · ${returnNote.when}` : ""}
            </AlertTitle>
            {returnNote.reason || "No note was left. Ask them what needs to change before re-submitting."}
          </Alert>
        )}

        {reopenError ? (
          <Box sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
            <Alert severity="error" data-testid="postif-reopen-error">
              {reopenError}
            </Alert>
          </Box>
        ) : hydrating ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", gap: 1.5 }}>
            <CircularProgress size={20} thickness={5} />
            <span>Opening the form…</span>
          </Box>
        ) : (
          <>
        {screen === "start" && (
          <PifStartScreen onConfirmed={handleConfirmed} onResume={handleResume} opening={opening} />
        )}
        {screen === "form" && (
          <PifWorkspace
            flow={flow}
            candidate={candidate}
            values={values}
            draft={draft}
            ensureDraftId={ensureCreated}
            catalogue={catalogue}
            eav={eav}
            history={history}
            onChange={handleFieldChange}
            onEavChange={handleEavChange}
            onSaved={handleSaved}
            onExit={handleExit}
            onSigned={handleSigned}
          />
        )}
        {screen === "done" && signed && (
          <PifDoneScreen
            icon={<CheckIcon />}
            tone="success"
            title="Signed and sent"
            lede="It's locked and with the Department Director now. HR reviews after that, and the Executive Director signs last — a completed copy comes back to you."
            // Epic 79824's generic done-screen props, fed the real reference number that
            // submit returned (or the one create-draft gave us) rather than a stub id.
            meta={[signed.refNo || draft?.refNo, signed.signedAt && `signed ${signed.signedAt}`]
              .filter(Boolean)
              .join(" · ")}
            chain={doneChain(`${firstName ?? ""} ${lastName ?? ""}`.trim() || userName || "—")}
            actions={[
              { label: "Copy link", variant: "outlined", onClick: handleCopyLink, testId: "postif-final-copylink" },
              { label: "Download PDF", variant: "outlined", onClick: handleDownloadPdf, testId: "postif-final-downloadpdf" },
              { label: "Start another", variant: "contained", onClick: handleRestart, testId: "postif-final-startanother" },
            ]}
          />
        )}
          </>
        )}

        <ConfirmationDialog
          open={confirmExit}
          title="Leave without saving?"
          message="This form hasn't been signed or saved as a draft. Your answers will be discarded."
          onClose={handleConfirmExit}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default PostInterviewForm;
